import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';
import { AppState } from '.';
import {
  Account,
  AccountRole,
  exceptionOf,
  SerializedException,
  User,
  Operator,
  Query, SalesforceAccount,
} from '../models';
import { AccountService, PromotionService } from '../services';
import { setAccountUuidHeader } from '../services/axios';

export const ACCOUNT_FEATURE_KEY = 'accounts';
interface AccountEntity extends Account {
  numberOrganizations?: number;
  numberAccountUsers?: number;
  numberProjects?: number;
}

const accountsAdapter = createEntityAdapter<AccountEntity>();

interface AccountState extends EntityState<AccountEntity> {
  loading: boolean;
  error?: SerializedException;
  g2Accounts: AccountEntity[];
  total: number;
  g2Fetching: boolean;
  g3Fetching: boolean;
  selectedAccountId: number | undefined;
  isPublicDomain: boolean;
  isOptedInStarterPackage: boolean;
  isOptInToSelfServe: boolean;
  isOptedInFirstThreeLicenses: boolean;
  emptyAccounts: AccountEntity[];
  awsLinkedAccount?: AccountEntity;
  salesforceAccount?: SalesforceAccount;
}

const createInitialState = (): AccountState => (
  accountsAdapter.getInitialState({
    loading: false,
    g2Accounts: [],
    total: 0,
    g2Fetching: true,
    g3Fetching: true,
    isPublicDomain: true,
    isOptedInStarterPackage: false,
    selectedAccountId: undefined,
    isOptInToSelfServe: false,
    isOptedInFirstThreeLicenses: false,
    rejectedAccounts: [],
    emptyAccounts: [],
    awsLinkedAccount: undefined,
    salesforceAccount: undefined,
  })
);

export const doGetUserAccount = createAsyncThunk(
  'account/getUserAccount',
  async (input: { id: User['id'], offset?: number }) => {
    const criteria: Array<Query<Account>> = [
      // { field: 'accountUsers.userId', operator: Operator.EQ, value: input.id },
      { field: 'archived', operator: Operator.EQ, value: false },
      { offset: input.offset ?? 0 },
    ];

    const { data, total } = await AccountService.getAccounts(...criteria);
    return {
      userId: input.id,
      accounts: data,
      total,
    };
  },
);

export const doGetG2Accounts = createAsyncThunk(
  'account/getG2Accounts',
  async (input: { id: User['id'], offset?: number }) => {
    const criteria: Array<Query<Account>> = [
      { field: 'accountUsers.userId', operator: Operator.EQ, value: input.id },
      { field: 'archived', operator: Operator.EQ, value: false },
      {
        operator: Operator.OR,
        value: [
          { field: 'accountUsers.role', operator: Operator.EQ, value: AccountRole.OWNER },
          { field: 'accountUsers.role', operator: Operator.EQ, value: AccountRole.BILLING_MANAGER },
        ],
      },
      { offset: input.offset ?? 0 },
    ];

    const { data, total } = await AccountService.getAccounts(...criteria);
    return {
      userId: input.id,
      accounts: data,
      total,
    };
  },
);

export const doCheckPublicDomain = createAsyncThunk(
  'account/checkPublicDomain',
  async (accountId: number, { rejectWithValue }) => {
    try {
      const data = await AccountService.checkPublicDomain({ id: accountId });
      return data;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doCheckStarterPackage = createAsyncThunk(
  'account/checkStarterPackage',
  async ({
    accountId,
    spEnabled,
  }: { accountId: number, spEnabled: boolean }, { rejectWithValue }) => {
    try {
      const data = await PromotionService.checkStarterPackage({ accountId, spEnabled });
      return data;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doCheckFirstThreeLicenses = createAsyncThunk(
  'account/checkFirstThreeLicenses',
  async (_, { rejectWithValue }) => {
    try {
      const data = await PromotionService.checkFirstThreeLicenses();
      return data;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doGetEmptyAccounts = createAsyncThunk(
  'account/getEmptyAccounts',
  async () => {
    const data = await AccountService.getAccountsWithoutSubscriptions();
    return data;
  },
);

export const doGetAwsLinkedAccount = createAsyncThunk(
  'account/getAwsLinkedAccount',
  async () => {
    const data = await AccountService.getAwsLinkedAccount();
    return data;
  },
);

export const doGetSalesforceLinkedAccount = createAsyncThunk(
  'account/getSalesforceLinkedAccount',
  async () => {
    const data = await AccountService.getSalesforceLinkedAccount();
    return { salesforceAccount: data };
  },
);

const accountsSlice = createSlice({
  name: ACCOUNT_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    doSetSelectedAccount: (state, action) => {
      const account = action.payload;
      state.selectedAccountId = account.id;
      setAccountUuidHeader(account.uuid);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(doGetUserAccount.pending, state => {
        state.loading = true;
        state.g3Fetching = true;
      })
      .addCase(doGetUserAccount.fulfilled, (state, action) => {
        state.loading = false;
        accountsAdapter.upsertMany(state, action.payload.accounts);
        state.total = action.payload.total;
        state.g3Fetching = state.ids.length < action.payload.total;
      })
      .addCase(doGetUserAccount.rejected, state => {
        state.loading = false;
      })
      .addCase(doGetG2Accounts.pending, state => {
        state.loading = true;
        state.g2Fetching = true;
      })
      .addCase(doGetG2Accounts.fulfilled, (state, action) => {
        state.loading = false;
        state.g2Accounts.push(...action.payload.accounts);
        state.g2Fetching = false;
      })
      .addCase(doGetG2Accounts.rejected, state => {
        state.loading = false;
      })
      .addCase(doCheckPublicDomain.pending, state => {
        state.loading = true;
      })
      .addCase(doCheckPublicDomain.fulfilled, (state, action) => {
        state.loading = false;
        // K1 returns {} (instead of a boolean) when its upstream
        // check-public-domain call fails, so only an explicit true counts.
        state.isPublicDomain = action.payload === true;
      })
      .addCase(doCheckPublicDomain.rejected, state => {
        state.loading = false;
      })
      .addCase(doCheckStarterPackage.pending, state => {
        state.loading = true;
      })
      .addCase(doCheckStarterPackage.fulfilled, (state, action) => {
        state.loading = false;
        state.isOptedInStarterPackage = action.payload ?? false;
      })
      .addCase(doCheckStarterPackage.rejected, state => {
        state.loading = false;
      })
      .addCase(doCheckFirstThreeLicenses.pending, state => {
        state.loading = true;
      })
      .addCase(doCheckFirstThreeLicenses.fulfilled, (state, action) => {
        state.loading = false;
        state.isOptedInFirstThreeLicenses = action.payload ?? false;
      })
      .addCase(doCheckFirstThreeLicenses.rejected, state => {
        state.loading = false;
      })
      .addCase(doGetEmptyAccounts.pending, state => {
        state.loading = true;
      })
      .addCase(doGetEmptyAccounts.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data.length > 0) {
          state.emptyAccounts = action.payload.data;
        }
      })
      .addCase(doGetEmptyAccounts.rejected, state => {
        state.loading = false;
      })
      .addCase(doGetAwsLinkedAccount.pending, state => {
        state.loading = true;
      })
      .addCase(doGetAwsLinkedAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.awsLinkedAccount = action.payload;
      })
      .addCase(doGetAwsLinkedAccount.rejected, state => {
        state.loading = false;
      })
      .addCase(doGetSalesforceLinkedAccount.pending, state => {
        state.loading = true;
      })
      .addCase(doGetSalesforceLinkedAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.salesforceAccount = action.payload.salesforceAccount;
      })
      .addCase(doGetSalesforceLinkedAccount.rejected, state => {
        state.loading = false;
      });
  },
});

const selectAccountFeature = (state: AppState) => state[ACCOUNT_FEATURE_KEY];
const { selectAll, selectEntities } = accountsAdapter.getSelectors(selectAccountFeature);

export const selectAccountById = (id: Account['id']) => createSelector(
  selectEntities,
  entities => entities[id],
);

export const selectSelectedAccountId = createSelector(
  selectAccountFeature,
  state => state.selectedAccountId,
);

export const selectSelectedAccount = createSelector(
  selectAll,
  selectSelectedAccountId,
  (allAccounts, selectedAccountId) => {
    if (selectedAccountId) {
      return allAccounts.find(account => account.id === selectedAccountId) ?? allAccounts[0];
    }
    return allAccounts[0];
  },
);

/**
 * The account the user (or the URL) has explicitly selected — no `allAccounts[0]` fallback.
 * Use for side effects that must not fire before a selection is made (analytics group/traits).
 */
export const selectExplicitlySelectedAccount = createSelector(
  selectEntities,
  selectSelectedAccountId,
  (entities, selectedAccountId) => (
    selectedAccountId ? entities[selectedAccountId] : undefined
  ),
);

export const selectUserAccounts = createSelector(
  selectAll,
  allAccounts => allAccounts ?? [],
);

export const selectUserG2Accounts = createSelector(
  selectAccountFeature,
  state => state.g2Accounts,
);

export const selectTotal = createSelector(
  selectAccountFeature,
  state => state.total,
);

export const selectLoading = createSelector(
  selectAccountFeature,
  state => state.loading,
);

export const selectG3Fetching = createSelector(
  selectAccountFeature,
  state => state.g3Fetching,
);

export const selectG2Fetching = createSelector(
  selectAccountFeature,
  state => state.g2Fetching,
);

export const selectIsPublicDomain = createSelector(
  selectAccountFeature,
  state => state.isPublicDomain,
);

export const selectIsOptedInFirstThreeLicenses = createSelector(
  selectAccountFeature,
  state => state.isOptedInFirstThreeLicenses,
);

export const selectEmptyAccounts = createSelector(
  selectAccountFeature,
  state => state.emptyAccounts,
);

export const selectAwsLinkedAccount = createSelector(
  selectAccountFeature,
  state => state.awsLinkedAccount,
);

export const selectSalesforceAccount = createSelector(
  selectAccountFeature,
  state => state.salesforceAccount,
);

export const {
  doSetSelectedAccount,
} = accountsSlice.actions;

export default accountsSlice.reducer;
